/**
 * Pipeline Page — Kanban board for Merchant Services, POS Systems, and Lending.
 * Drag-and-drop cards between stages. Consistent UI across all pipeline types.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import MerchantContextPanel from '../components/MerchantContextPanel';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Plus, Search, MoreHorizontal, GripVertical, User as UserIcon,
  DollarSign, FileText, Building2, Send, Eye, Play, Clock,
  AlertTriangle, ArrowRightLeft, Trash2, X, ChevronRight, Mail,
  UserCheck, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import AssignmentRulesDialog from './AssignmentRulesDialog';

// ── Stage colors for visual distinction ──────────────────────
const STAGE_COLORS = {
  // Merchant Services
  'New Deal': '#3B82F6', 'Contacted': '#6366F1', 'Qualified': '#8B5CF6',
  'Application Sent': '#A855F7', 'Docs Collection': '#F59E0B',
  'Underwriting': '#EF4444', 'Approved': '#10B981', 'Equipment Ordered': '#14B8A6',
  'Installed': '#06B6D4', 'Live': '#22C55E',
  // Lending
  'New Application': '#3B82F6', 'Pre-Qualified': '#8B5CF6',
  'Docs Requested': '#F59E0B', 'Docs Received': '#F97316',
  'Submitted': '#EF4444', 'Offers Received': '#14B8A6',
  'Accepted': '#10B981', 'Funded': '#22C55E',
  // POS
  'New Lead': '#3B82F6', 'Demo Scheduled': '#A855F7',
  'Proposal Sent': '#F59E0B', 'Closed Won': '#10B981',
  'Order Sent': '#14B8A6', 'Provisioning': '#06B6D4',
};

const TAG_COLORS = {
  'Retail': 'bg-blue-100 text-blue-700', 'Restaurant': 'bg-red-100 text-red-700',
  'B2B': 'bg-yellow-100 text-yellow-800', 'E-Commerce': 'bg-purple-100 text-purple-700',
  'Healthcare': 'bg-emerald-100 text-emerald-700', 'Hospitality': 'bg-orange-100 text-orange-700',
  'Term Loan': 'bg-blue-100 text-blue-700', 'MCA': 'bg-indigo-100 text-indigo-700',
  'SBA': 'bg-green-100 text-green-700', 'Line of Credit': 'bg-cyan-100 text-cyan-700',
  'Hardware': 'bg-orange-100 text-orange-700', 'Software Only': 'bg-violet-100 text-violet-700',
  'Full System': 'bg-emerald-100 text-emerald-700', 'Upgrade': 'bg-amber-100 text-amber-700',
};

const PIPELINE_META = {
  merchant_services: {
    title: 'Merchant Services Pipeline',
    subtitle: 'Manage and track merchant acquiring deals',
    newBtn: '+ New Deal',
    searchPlaceholder: 'Search deals, merchants...',
  },
  lending: {
    title: 'Lending Pipeline',
    subtitle: 'Loan applications, underwriting, and funding status',
    newBtn: '+ New Application',
    searchPlaceholder: 'Search applications, SSN...',
  },
  pos: {
    title: 'POS Systems Pipeline',
    subtitle: 'Hardware deployment and software integration tracking',
    newBtn: '+ New Order',
    searchPlaceholder: 'Search serials, stores...',
  },
};

export default function PipelinePage() {
  const { pipelineType } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const pipeline = pipelineType || 'merchant_services';

  const [config, setConfig] = useState(null);
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('open');
  const [showNewDeal, setShowNewDeal] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const boardRef = useRef(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'iso';

  const meta = PIPELINE_META[pipeline] || PIPELINE_META.merchant_services;

  const loadConfig = useCallback(async () => {
    try {
      const c = await api.request('/pipeline/config');
      setConfig(c);
    } catch {}
  }, []);

  const loadDeals = useCallback(async () => {
    try {
      const params = new URLSearchParams({ pipeline });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterAssignee !== 'all') params.append('assigned_user_id', filterAssignee);
      if (search) params.append('search', search);
      const d = await api.request(`/pipeline/deals?${params}`);
      setDeals(d || []);
    } catch {}
  }, [pipeline, filterStatus, filterAssignee, search]);

  const loadStats = useCallback(async () => {
    try {
      const s = await api.request(`/pipeline/stats?pipeline=${pipeline}`);
      setStats(s);
    } catch {}
  }, [pipeline]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadDeals(); loadStats(); }, [loadDeals, loadStats]);

  const stages = config?.pipelines?.[pipeline] || [];

  // ── Drag & Drop Handlers ────────────────────────────────────
  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', deal.id);
    // Make ghost semi-transparent
    if (e.target) {
      setTimeout(() => { e.target.style.opacity = '0.4'; }, 0);
    }
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedDeal(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedDeal || draggedDeal.stage === targetStage) return;

    // Optimistic update
    setDeals(prev => prev.map(d =>
      d.id === draggedDeal.id ? { ...d, stage: targetStage } : d
    ));

    try {
      await api.request(`/pipeline/deals/${draggedDeal.id}/stage`, {
        method: 'PUT',
        body: JSON.stringify({ new_stage: targetStage }),
      });
      toast.success(`Moved "${draggedDeal.merchant_name}" to ${targetStage}`);
      loadStats();
    } catch (err) {
      toast.error(err.message || 'Failed to move deal');
      loadDeals(); // Revert on error
    }
    setDraggedDeal(null);
  };

  const deleteDeal = async (dealId) => {
    if (!window.confirm('Delete this deal permanently?')) return;
    try {
      await api.request(`/pipeline/deals/${dealId}`, { method: 'DELETE' });
      toast.success('Deal deleted');
      setSelectedDeal(null);
      loadDeals();
      loadStats();
    } catch (err) { toast.error(err.message); }
  };

  // ── Stats Bar ───────────────────────────────────────────────
  const renderStats = () => {
    if (!stats) return null;
    if (pipeline === 'merchant_services') {
      return (
        <div className="flex items-center gap-4 text-sm">
          <span className="text-primary font-semibold">Total Value: ${((stats.total_value || 0) / 1000).toFixed(0)}K</span>
          <span className="font-semibold">{stats.active_deals || 0} Active Deals</span>
        </div>
      );
    }
    if (pipeline === 'lending') {
      return (
        <div className="flex items-center gap-4 text-sm">
          <span className="text-red-600 font-semibold">Urgent: {stats.urgent || 0}</span>
          <span className="text-amber-600 font-semibold">Pending Docs: {stats.pending_docs || 0}</span>
          <span className="text-green-600 font-semibold">Fundable: {stats.fundable || 0}</span>
        </div>
      );
    }
    if (pipeline === 'pos') {
      return (
        <div className="flex items-center gap-4 text-sm">
          <span className="text-blue-600 font-semibold">Pending Shipments: {stats.pending_shipments || 0}</span>
          <span className="text-green-600 font-semibold">Active Installs: {stats.active_installs || 0}</span>
        </div>
      );
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b bg-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold" data-testid="pipeline-title">{meta.title}</h1>
            <p className="text-sm text-muted-foreground">{meta.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={meta.searchPlaceholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 w-52"
                data-testid="pipeline-search"
              />
            </div>
            <Button onClick={() => setShowNewDeal(true)} data-testid="new-deal-btn">
              <Plus className="h-4 w-4 mr-2" /> {meta.newBtn}
            </Button>
            {isAdmin && (
              <Button variant="outline" onClick={() => setShowRules(true)} data-testid="assignment-rules-btn">
                <Zap className="h-4 w-4 mr-2" /> Rules
              </Button>
            )}
          </div>
        </div>

        {/* Filters + Stats */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-3 gap-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground font-medium">Filter by:</span>
            <select
              value={filterAssignee}
              onChange={e => setFilterAssignee(e.target.value)}
              className="text-sm border rounded-lg px-2.5 py-1.5 bg-background"
              data-testid="filter-assignee"
            >
              <option value="all">All Assignees</option>
              <option value={user?.id}>My Deals</option>
            </select>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="text-sm border rounded-lg px-2.5 py-1.5 bg-background"
              data-testid="filter-status"
            >
              <option value="open">Open</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
              <option value="all">All Status</option>
            </select>
          </div>
          {renderStats()}
        </div>
      </div>

      {/* Kanban Board */}
      <div
        ref={boardRef}
        className="flex-1 overflow-x-auto overflow-y-hidden"
        data-testid="kanban-board"
      >
        <div className="flex h-full p-4 gap-4" style={{ minWidth: stages.length * 280 }}>
          {stages.map(stage => {
            const stageDeals = deals.filter(d => d.stage === stage);
            const isOver = dragOverStage === stage;
            const color = STAGE_COLORS[stage] || '#6B7280';

            return (
              <div
                key={stage}
                className={`flex-shrink-0 w-[272px] flex flex-col rounded-xl transition-all duration-150 ${
                  isOver ? 'bg-primary/5 ring-2 ring-primary/30' : 'bg-muted/30'
                }`}
                onDragOver={(e) => handleDragOver(e, stage)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage)}
                data-testid={`stage-column-${stage.replace(/\s/g, '-')}`}
              >
                {/* Stage Header */}
                <div className="flex items-center justify-between px-3 py-3 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-sm font-semibold truncate">{stage}</span>
                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded-md font-medium" data-testid={`stage-count-${stage.replace(/\s/g, '-')}`}>
                      {stageDeals.length}
                    </span>
                  </div>
                  <button className="p-1 rounded hover:bg-muted text-muted-foreground">
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </div>

                {/* Cards Container */}
                <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-[100px]">
                  {stageDeals.map(deal => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      pipeline={pipeline}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onClick={() => setSelectedDeal(deal)}
                    />
                  ))}

                  {/* Drop zone indicator */}
                  {isOver && stageDeals.length === 0 && (
                    <div className="border-2 border-dashed border-primary/30 rounded-lg p-6 text-center">
                      <p className="text-xs text-primary/60 font-medium">Drop here</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* New Deal Dialog */}
      <NewDealDialog
        open={showNewDeal}
        onClose={() => setShowNewDeal(false)}
        pipeline={pipeline}
        config={config}
        onCreated={() => { loadDeals(); loadStats(); setShowNewDeal(false); }}
      />

      {/* Deal Detail Dialog */}
      <DealDetailDialog
        deal={selectedDeal}
        pipeline={pipeline}
        stages={stages}
        config={config}
        onClose={() => setSelectedDeal(null)}
        onUpdate={() => { loadDeals(); loadStats(); }}
        onDelete={deleteDeal}
      />

      {/* Assignment Rules Dialog */}
      <AssignmentRulesDialog
        open={showRules}
        onClose={() => setShowRules(false)}
        pipelineType={pipeline}
      />
    </div>
  );
}


/* ═══════════════════════════════════════════
   DEAL CARD
   ═══════════════════════════════════════════ */
function DealCard({ deal, pipeline, onDragStart, onDragEnd, onClick }) {
  const tagLabel = pipeline === 'lending'
    ? deal.loan_type
    : pipeline === 'pos'
      ? deal.pos_type
      : deal.business_type;

  const tagColor = TAG_COLORS[tagLabel] || 'bg-gray-100 text-gray-700';

  const initials = (deal.merchant_name || '')
    .split(' ')
    .map(w => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, deal)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className="bg-card border rounded-lg p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group"
      data-testid={`deal-card-${deal.id}`}
    >
      {/* Header: ID + Tag */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] text-muted-foreground font-mono">#{deal.deal_id}</span>
        {tagLabel && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${tagColor}`}>
            {tagLabel}
          </span>
        )}
      </div>

      {/* Merchant Name */}
      <h4 className="text-sm font-semibold mb-1 truncate">{deal.merchant_name}</h4>

      {/* Pipeline-specific info */}
      {pipeline === 'merchant_services' && deal.monthly_volume > 0 && (
        <p className="text-xs text-muted-foreground mb-2">
          ${deal.monthly_volume.toLocaleString()} / mo volume
        </p>
      )}
      {pipeline === 'lending' && deal.loan_amount > 0 && (
        <p className="text-xs text-muted-foreground mb-2">
          Request: ${deal.loan_amount.toLocaleString()}{deal.loan_term ? ` (${deal.loan_term})` : ''}
        </p>
      )}
      {pipeline === 'pos' && (
        <p className="text-xs text-muted-foreground mb-2">
          {deal.equipment_details || 'No equipment specified'}
          {deal.equipment_value > 0 && <span className="float-right font-medium">${deal.equipment_value.toLocaleString()}</span>}
        </p>
      )}

      {/* Doc status indicators */}
      {deal.documents_status && Object.keys(deal.documents_status).length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {Object.entries(deal.documents_status).map(([doc, status]) => (
            <span key={doc} className={`text-[9px] flex items-center gap-0.5 ${
              status === 'approved' ? 'text-green-600' : status === 'uploaded' ? 'text-blue-600' : 'text-red-500'
            }`}>
              {status === 'approved' ? '✓' : status === 'uploaded' ? '◉' : '●'} {doc}
            </span>
          ))}
        </div>
      )}

      {/* Recent Activity Timeline */}
      {deal.recent_activity?.length > 0 && (
        <div className="mb-2 space-y-0.5" data-testid={`deal-activity-preview-${deal.id}`}>
          {deal.recent_activity.slice(0, 2).map((a, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[9px]">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                a.action_type === 'sms' ? 'bg-green-500' :
                a.action_type === 'email' ? 'bg-blue-500' :
                a.action_type === 'auto' ? 'bg-purple-500' :
                a.action_type === 'doc_upload' ? 'bg-amber-500' :
                a.action_type === 'onboarding' ? 'bg-cyan-500' :
                'bg-gray-400'
              }`} />
              <span className="text-muted-foreground truncate flex-1">{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Footer: Assignee + Time */}
      <div className="flex items-center justify-between mt-1 pt-1.5 border-t border-border/50">
        <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
          <span className="text-[8px] font-bold text-primary">{initials}</span>
        </div>
        {deal.created_at && (
          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
            <Clock className="h-2.5 w-2.5" />
            {formatTimeAgo(deal.created_at)}
          </span>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   NEW DEAL DIALOG
   ═══════════════════════════════════════════ */
function NewDealDialog({ open, onClose, pipeline, config, onCreated }) {
  const [form, setForm] = useState({
    merchant_name: '', business_type: '', monthly_volume: '',
    source: '', notes: '', loan_type: '', loan_amount: '',
    loan_term: '', pos_type: '', equipment_details: '', equipment_value: '',
  });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.merchant_name.trim()) {
      toast.error('Merchant name is required');
      return;
    }
    setSaving(true);
    try {
      await api.request('/pipeline/deals', {
        method: 'POST',
        body: JSON.stringify({
          pipeline,
          merchant_name: form.merchant_name,
          business_type: form.business_type,
          monthly_volume: parseFloat(form.monthly_volume) || 0,
          source: form.source,
          notes: form.notes,
          loan_type: form.loan_type,
          loan_amount: parseFloat(form.loan_amount) || 0,
          loan_term: form.loan_term,
          pos_type: form.pos_type,
          equipment_details: form.equipment_details,
          equipment_value: parseFloat(form.equipment_value) || 0,
        }),
      });
      toast.success('Deal created');
      setForm({ merchant_name: '', business_type: '', monthly_volume: '', source: '', notes: '',
        loan_type: '', loan_amount: '', loan_term: '', pos_type: '', equipment_details: '', equipment_value: '' });
      onCreated();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const businessTypes = config?.business_types || [];
  const loanTypes = config?.loan_types || [];
  const posTypes = config?.pos_types || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="new-deal-dialog">
        <DialogHeader>
          <DialogTitle>{PIPELINE_META[pipeline]?.newBtn || '+ New Deal'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <Label>Merchant / Business Name *</Label>
            <Input value={form.merchant_name} onChange={e => setForm({...form, merchant_name: e.target.value})} className="mt-1" placeholder="Apex Coffee Roasters" data-testid="deal-merchant-name" />
          </div>

          {/* Merchant Services fields */}
          {pipeline === 'merchant_services' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Business Type</Label>
                  <select value={form.business_type} onChange={e => setForm({...form, business_type: e.target.value})} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="deal-biz-type">
                    <option value="">Select...</option>
                    {businessTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Monthly Volume ($)</Label>
                  <Input type="number" value={form.monthly_volume} onChange={e => setForm({...form, monthly_volume: e.target.value})} className="mt-1" placeholder="15000" data-testid="deal-volume" />
                </div>
              </div>
            </>
          )}

          {/* Lending fields */}
          {pipeline === 'lending' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Loan Type</Label>
                  <select value={form.loan_type} onChange={e => setForm({...form, loan_type: e.target.value})} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="deal-loan-type">
                    <option value="">Select...</option>
                    {loanTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Loan Amount ($)</Label>
                  <Input type="number" value={form.loan_amount} onChange={e => setForm({...form, loan_amount: e.target.value})} className="mt-1" placeholder="50000" data-testid="deal-loan-amount" />
                </div>
              </div>
              <div>
                <Label>Loan Term</Label>
                <Input value={form.loan_term} onChange={e => setForm({...form, loan_term: e.target.value})} className="mt-1" placeholder="36 months" data-testid="deal-loan-term" />
              </div>
            </>
          )}

          {/* POS fields */}
          {pipeline === 'pos' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>POS Type</Label>
                  <select value={form.pos_type} onChange={e => setForm({...form, pos_type: e.target.value})} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="deal-pos-type">
                    <option value="">Select...</option>
                    {posTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Equipment Value ($)</Label>
                  <Input type="number" value={form.equipment_value} onChange={e => setForm({...form, equipment_value: e.target.value})} className="mt-1" placeholder="3450" data-testid="deal-equip-value" />
                </div>
              </div>
              <div>
                <Label>Equipment Details</Label>
                <Input value={form.equipment_details} onChange={e => setForm({...form, equipment_details: e.target.value})} className="mt-1" placeholder="2x Terminals + 1x Kitchen Display" data-testid="deal-equip-details" />
              </div>
            </>
          )}

          <div>
            <Label>Source</Label>
            <Input value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="mt-1" placeholder="Referral, Cold Call, Website..." data-testid="deal-source" />
          </div>
          <div>
            <Label>Notes</Label>
            <textarea
              value={form.notes}
              onChange={e => setForm({...form, notes: e.target.value})}
              rows={2}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              placeholder="Any additional details..."
              data-testid="deal-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} data-testid="create-deal-btn">
            {saving ? 'Creating...' : 'Create Deal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ═══════════════════════════════════════════
   DEAL DETAIL DIALOG
   ═══════════════════════════════════════════ */
function DealDetailDialog({ deal, pipeline, stages, config, onClose, onUpdate, onDelete }) {
  const { user } = useAuthStore();
  const [tab, setTab] = useState('overview');
  const [transferOpen, setTransferOpen] = useState(false);
  const [targetPipeline, setTargetPipeline] = useState('');
  const [targetStage, setTargetStage] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Docs state
  const [docsData, setDocsData] = useState(null);
  const [uploading, setUploading] = useState(null);

  // Activity state
  const [activity, setActivity] = useState([]);

  // Assignment state
  const [orgUsers, setOrgUsers] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'iso';

  useEffect(() => {
    if (deal) {
      loadDocs();
      loadActivity();
      if (isAdmin) loadOrgUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, tab]);

  const loadOrgUsers = async () => {
    try {
      const users = await api.request('/pipeline/org-users');
      setOrgUsers(users || []);
    } catch {}
  };

  const loadDocs = async () => {
    if (!deal) return;
    try {
      const data = await api.request(`/pipeline/deals/${deal.id}/documents`);
      setDocsData(data);
    } catch {}
  };

  const loadActivity = async () => {
    if (!deal) return;
    try {
      const data = await api.request(`/pipeline/deals/${deal.id}/activity`);
      setActivity(data || []);
    } catch {}
  };

  if (!deal) return null;

  const handleStageChange = async (newStage) => {
    try {
      await api.request(`/pipeline/deals/${deal.id}/stage`, {
        method: 'PUT',
        body: JSON.stringify({ new_stage: newStage }),
      });
      toast.success(`Moved to ${newStage}`);
      onUpdate();
      loadActivity();
    } catch (err) { toast.error(err.message); }
  };

  const handleTransfer = async () => {
    if (!targetPipeline || !targetStage) return;
    setTransferring(true);
    try {
      await api.request(`/pipeline/deals/${deal.id}/transfer`, {
        method: 'PUT',
        body: JSON.stringify({ target_pipeline: targetPipeline, target_stage: targetStage, tag: `from_${pipeline}` }),
      });
      toast.success(`Transferred to ${targetPipeline}`);
      setTransferOpen(false);
      onUpdate();
      onClose();
    } catch (err) { toast.error(err.message); }
    setTransferring(false);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.request(`/pipeline/deals/${deal.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(`Status updated to ${newStatus}`);
      onUpdate();
    } catch (err) { toast.error(err.message); }
  };

  const handleDocUpload = async (slot, file) => {
    setUploading(slot);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pipeline/deals/${deal.id}/documents/${slot}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      toast.success('Document uploaded');
      loadDocs();
      loadActivity();
      onUpdate();
    } catch (err) { toast.error(err.message); }
    setUploading(null);
  };

  const handleDocReview = async (slot, action) => {
    try {
      await api.request(`/pipeline/deals/${deal.id}/documents/${slot}/review?action=${action}`, { method: 'PUT' });
      toast.success(`Document ${action}`);
      loadDocs();
      onUpdate();
    } catch (err) { toast.error(err.message); }
  };

  const generateOnboardingLink = async () => {
    try {
      const data = await api.request(`/pipeline/deals/${deal.id}/onboarding/generate-link`, { method: 'POST' });
      toast.success(`Onboarding link: ${data.onboarding_link}`);
      loadActivity();
      onUpdate();
    } catch (err) { toast.error(err.message); }
  };

  const generateUploadLink = async () => {
    try {
      const data = await api.request(`/pipeline/deals/${deal.id}/upload-link`, { method: 'POST' });
      toast.success(`Upload link: ${data.upload_link}`);
      loadActivity();
    } catch (err) { toast.error(err.message); }
  };

  const handleAssign = async (userId) => {
    setAssigning(true);
    try {
      await api.request(`/pipeline/deals/${deal.id}/assign`, {
        method: 'PUT',
        body: JSON.stringify({ assigned_user_id: userId }),
      });
      toast.success('Deal reassigned');
      onUpdate();
    } catch (err) { toast.error(err.message); }
    setAssigning(false);
  };

  const currentIdx = stages.indexOf(deal.stage);
  const pipelines = config?.pipelines || {};
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'documents', label: 'Documents', badge: docsData?.stats?.uploaded },
    { id: 'activity', label: 'Activity', badge: activity.length },
  ];

  return (
    <Dialog open={!!deal} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="deal-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm font-mono">#{deal.deal_id}</span>
            <span>{deal.merchant_name}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ml-auto ${
              deal.status === 'won' ? 'bg-green-100 text-green-700' :
              deal.status === 'lost' ? 'bg-red-100 text-red-700' :
              'bg-blue-100 text-blue-700'
            }`}>{deal.status}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Stage Progress Bar */}
        <div className="flex flex-wrap gap-1 pb-3 border-b">
          {stages.map((s, i) => {
            const isCurrent = s === deal.stage;
            const isPast = i < currentIdx;
            return (
              <button key={s} onClick={() => handleStageChange(s)}
                className={`text-[10px] px-2 py-0.5 rounded-md font-medium transition-colors ${
                  isCurrent ? 'bg-primary text-white' : isPast ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`} data-testid={`stage-btn-${s.replace(/\s/g, '-')}`}
              >{s}</button>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`} data-testid={`deal-tab-${t.id}`}
            >
              {t.label}
              {t.badge > 0 && <span className="ml-1.5 text-[10px] bg-muted px-1.5 py-0.5 rounded-full">{t.badge}</span>}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto py-3 space-y-4">
          {/* ── OVERVIEW TAB ── */}
          {tab === 'overview' && (
            <>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    {deal.status === 'open' && (
                      <>
                        <button onClick={() => handleStatusChange('won')} className="text-xs text-green-600 hover:underline font-medium">Mark Won</button>
                        <button onClick={() => handleStatusChange('lost')} className="text-xs text-red-600 hover:underline font-medium">Mark Lost</button>
                      </>
                    )}
                  </div>
                </div>
                {deal.business_type && <InfoField label="Business Type" value={deal.business_type} />}
                {deal.monthly_volume > 0 && <InfoField label="Monthly Volume" value={`$${deal.monthly_volume.toLocaleString()}`} />}
                {deal.loan_amount > 0 && <InfoField label="Loan Amount" value={`$${deal.loan_amount.toLocaleString()} ${deal.loan_term ? `(${deal.loan_term})` : ''}`} />}
                {deal.loan_type && <InfoField label="Loan Type" value={deal.loan_type} />}
                {deal.equipment_value > 0 && <InfoField label="Equipment Value" value={`$${deal.equipment_value.toLocaleString()}`} />}
                {deal.source && <InfoField label="Source" value={deal.source} />}
                <InfoField label="Onboarding" value={deal.onboarding_status || 'not_started'} />
              </div>

              {deal.notes && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm bg-muted/30 rounded-lg p-3">{deal.notes}</p>
                </div>
              )}

              {deal.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {deal.tags.map(tag => <span key={tag} className="text-[10px] px-2 py-0.5 bg-muted rounded-full font-medium">{tag}</span>)}
                </div>
              )}

              {/* Assignment Section (Admin Only) */}
              {isAdmin && (
                <div className="border rounded-lg p-3 space-y-2 bg-muted/10" data-testid="deal-assignment-section">
                  <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                    <UserCheck className="h-3.5 w-3.5" /> Assigned To
                  </div>
                  <select
                    value={deal.assigned_user_id || ''}
                    onChange={e => handleAssign(e.target.value)}
                    disabled={assigning}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                    data-testid="deal-assign-dropdown"
                  >
                    <option value="">Unassigned</option>
                    {orgUsers.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                  </select>
                  {assigning && <span className="text-[10px] text-primary">Reassigning...</span>}
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button size="sm" variant="outline" onClick={generateOnboardingLink} data-testid="gen-onboarding-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Send Onboarding Link
                </Button>
                <Button size="sm" variant="outline" onClick={generateUploadLink} data-testid="gen-upload-btn">
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> Send Upload Link
                </Button>
                <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)} data-testid="transfer-deal-btn">
                  <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" /> Transfer
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => onDelete(deal.id)} data-testid="delete-deal-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </Button>
              </div>

              {/* Transfer Panel */}
              {transferOpen && (
                <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <ArrowRightLeft className="h-4 w-4" /> Transfer to Another Pipeline
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Target Pipeline</Label>
                      <select value={targetPipeline} onChange={e => { setTargetPipeline(e.target.value); setTargetStage(''); }}
                        className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="transfer-pipeline-select">
                        <option value="">Select...</option>
                        {Object.keys(pipelines).filter(p => p !== pipeline).map(p => (
                          <option key={p} value={p}>{p.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Target Stage</Label>
                      <select value={targetStage} onChange={e => setTargetStage(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" data-testid="transfer-stage-select" disabled={!targetPipeline}>
                        <option value="">Select...</option>
                        {(pipelines[targetPipeline] || []).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleTransfer} disabled={transferring || !targetStage} data-testid="confirm-transfer-btn">
                      {transferring ? 'Transferring...' : 'Transfer'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* Stage History */}
              {deal.stage_history?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Stage History</p>
                  <div className="space-y-1 max-h-28 overflow-y-auto">
                    {deal.stage_history.slice().reverse().map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        <span className="font-medium">{h.stage}</span>
                        <span className="text-muted-foreground">{new Date(h.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Canonical merchant context — same component/API as Leads & Applications */}
              <MerchantContextPanel businessId={deal.business_id} />
            </>
          )}

          {/* ── DOCUMENTS TAB ── */}
          {tab === 'documents' && (
            <>
              {/* Doc Stats */}
              {docsData?.stats && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-medium">{docsData.stats.uploaded}/{docsData.stats.total} uploaded</span>
                  <span className="text-green-600 font-medium">{docsData.stats.approved} approved</span>
                  {docsData.stats.complete && (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">All docs complete</span>
                  )}
                </div>
              )}

              {/* Document Slots */}
              <div className="space-y-2">
                {(docsData?.documents || []).map(doc => (
                  <div key={doc.slot} className={`flex items-center gap-3 p-3 rounded-lg border ${
                    doc.status === 'approved' ? 'border-green-200 bg-green-50/30' :
                    doc.status === 'uploaded' ? 'border-blue-200 bg-blue-50/30' :
                    doc.status === 'rejected' ? 'border-red-200 bg-red-50/30' :
                    'border-border'
                  }`} data-testid={`doc-slot-${doc.slot}`}>
                    {/* Status Icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      doc.status === 'approved' ? 'bg-green-100' :
                      doc.status === 'uploaded' ? 'bg-blue-100' :
                      doc.status === 'rejected' ? 'bg-red-100' :
                      'bg-muted'
                    }`}>
                      {doc.status === 'approved' ? <span className="text-green-600 text-sm">&#10003;</span> :
                       doc.status === 'uploaded' ? <FileText className="h-4 w-4 text-blue-600" /> :
                       doc.status === 'rejected' ? <X className="h-4 w-4 text-red-600" /> :
                       <FileText className="h-4 w-4 text-muted-foreground/40" />}
                    </div>

                    {/* Label + Status */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{doc.label}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {doc.required ? 'Required' : 'Optional'}
                        {doc.file_name && <span> &bull; {doc.file_name}</span>}
                        {doc.uploaded_at && <span> &bull; {new Date(doc.uploaded_at).toLocaleDateString()}</span>}
                      </p>
                    </div>

                    {/* Status Badge */}
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                      doc.status === 'approved' ? 'bg-green-100 text-green-700' :
                      doc.status === 'uploaded' ? 'bg-blue-100 text-blue-700' :
                      doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>{doc.status}</span>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {doc.status === 'uploaded' && (
                        <>
                          <button onClick={() => handleDocReview(doc.slot, 'approved')}
                            className="text-[10px] px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100 font-medium" data-testid={`approve-doc-${doc.slot}`}>
                            Approve
                          </button>
                          <button onClick={() => handleDocReview(doc.slot, 'rejected')}
                            className="text-[10px] px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 font-medium" data-testid={`reject-doc-${doc.slot}`}>
                            Reject
                          </button>
                        </>
                      )}
                      {(doc.status === 'missing' || doc.status === 'rejected') && (
                        <label className="text-[10px] px-2 py-1 bg-primary/10 text-primary rounded hover:bg-primary/20 font-medium cursor-pointer" data-testid={`upload-doc-${doc.slot}`}>
                          {uploading === doc.slot ? 'Uploading...' : 'Upload'}
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={e => { if (e.target.files[0]) handleDocUpload(doc.slot, e.target.files[0]); }}
                            disabled={uploading === doc.slot} />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Upload Link Action */}
              <div className="pt-2 border-t">
                <Button size="sm" variant="outline" onClick={generateUploadLink} data-testid="send-upload-link-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Send Upload Link to Merchant
                </Button>
              </div>
            </>
          )}

          {/* ── ACTIVITY TAB ── */}
          {tab === 'activity' && (
            <>
              {activity.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
                  <p className="text-sm text-muted-foreground">No activity yet. Move the deal to a new stage to trigger automations.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activity.map((a, i) => (
                    <div key={a.id || i} className="flex items-start gap-3 p-3 rounded-lg border" data-testid={`activity-${i}`}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        a.action_type === 'sms' ? 'bg-green-50' :
                        a.action_type === 'email' ? 'bg-blue-50' :
                        a.action_type === 'auto' ? 'bg-purple-50' :
                        a.action_type === 'doc_upload' ? 'bg-amber-50' :
                        a.action_type === 'onboarding' ? 'bg-cyan-50' :
                        'bg-muted'
                      }`}>
                        {a.action_type === 'sms' ? <MessageSquareIcon className="h-3.5 w-3.5 text-green-600" /> :
                         a.action_type === 'email' ? <Mail className="h-3.5 w-3.5 text-blue-600" /> :
                         a.action_type === 'auto' ? <Play className="h-3.5 w-3.5 text-purple-600" /> :
                         a.action_type === 'doc_upload' ? <FileText className="h-3.5 w-3.5 text-amber-600" /> :
                         a.action_type === 'onboarding' ? <Send className="h-3.5 w-3.5 text-cyan-600" /> :
                         <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                            a.action_type === 'sms' ? 'bg-green-100 text-green-700' :
                            a.action_type === 'email' ? 'bg-blue-100 text-blue-700' :
                            a.action_type === 'auto' ? 'bg-purple-100 text-purple-700' :
                            a.action_type === 'doc_upload' ? 'bg-amber-100 text-amber-700' :
                            'bg-muted text-muted-foreground'
                          }`}>{a.action_type}</span>
                          <span className="text-[10px] text-muted-foreground">
                            Stage: {a.stage} &bull; {new Date(a.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm mt-0.5">{a.message}</p>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                        a.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>{a.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium mt-0.5 text-sm">{value}</p>
    </div>
  );
}

function MessageSquareIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}


/* ── Utility ─────────────────────────────────────────────── */
function formatTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
